/**
 * BookingDialog — Einzel- oder Serienbuchung eines Raums.
 */
import React from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Calendar, DoorOpen, Repeat, CalendarRange, Check, X, Loader2 } from 'lucide-react';
import { DatePickerDE } from '../../components/ui/date-picker-de';

const WEEKDAYS = [
  { value: 1, label: 'Mo' }, { value: 2, label: 'Di' }, { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' }, { value: 5, label: 'Fr' }, { value: 6, label: 'Sa' },
  { value: 7, label: 'So' },
];

export const BookingDialog = ({
  open, onOpenChange, selectedRoom, selectedDate,
  bookingForm, setBookingForm,
  saving, onBook,
  isSlotAvailable, generateSeriesDates, getSeriesConflicts,
}) => {
  if (!selectedRoom || !selectedDate) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  const setField = (field, value) =>
    setBookingForm((prev) => ({ ...prev, [field]: value }));

  const toggleWeekday = (val) => {
    const current = bookingForm.series_weekdays;
    const next = current.includes(val) ? current.filter((d) => d !== val) : [...current, val];
    setField('series_weekdays', next);
  };

  const seriesDateCount = bookingForm.is_series ? generateSeriesDates().length : 1;
  const seriesConflictCount = bookingForm.is_series ? getSeriesConflicts().length : 0;
  const slotAvailable = isSlotAvailable(selectedRoom.id, selectedDate, bookingForm.start_time, bookingForm.end_time);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Raum buchen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: selectedRoom.color || '#6366f1' }}
            >
              <DoorOpen className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">{selectedRoom.name}</p>
              <p className="text-sm text-muted-foreground">
                {format(selectedDate, 'EEEE, dd. MMMM yyyy', { locale: de })}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Startzeit</Label>
              <Input type="time" value={bookingForm.start_time}
                onChange={(e) => setField('start_time', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Endzeit</Label>
              <Input type="time" value={bookingForm.end_time}
                onChange={(e) => setField('end_time', e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Titel / Anlass *</Label>
            <Input
              placeholder="z.B. Team-Meeting, Schulung..."
              value={bookingForm.title}
              onChange={(e) => setField('title', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Beschreibung (optional)</Label>
            <Textarea
              placeholder="Weitere Details..."
              value={bookingForm.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={2}
            />
          </div>

          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="is_series"
                checked={bookingForm.is_series}
                onCheckedChange={(checked) => setField('is_series', checked)}
              />
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="is_series" className="cursor-pointer font-medium">
                  Serienbuchung
                </Label>
              </div>
            </div>

            {bookingForm.is_series && (
              <div className="space-y-4 pt-2 border-t">
                <div className="space-y-2">
                  <Label>Wiederholung</Label>
                  <Select
                    value={bookingForm.series_type}
                    onValueChange={(v) => setField('series_type', v)}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Täglich (ausgewählte Wochentage)</SelectItem>
                      <SelectItem value="weekly">Wöchentlich (gleicher Wochentag)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {bookingForm.series_type === 'daily' && (
                  <div className="space-y-2">
                    <Label>Wochentage</Label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAYS.map((day) => (
                        <Button
                          key={day.value}
                          type="button"
                          variant={bookingForm.series_weekdays.includes(day.value) ? 'default' : 'outline'}
                          size="sm"
                          className="w-10"
                          onClick={() => toggleWeekday(day.value)}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Serie endet am</Label>
                  <DatePickerDE
                    value={bookingForm.series_end_date}
                    onChange={(v) => setField('series_end_date', v)}
                    min={format(selectedDate, 'yyyy-MM-dd')}
                    placeholder="Enddatum"
                  />
                </div>

                {bookingForm.series_end_date && (
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <CalendarRange className="h-4 w-4" />
                      Vorschau: {seriesDateCount} Termine
                    </div>
                    {seriesConflictCount > 0 ? (
                      <div className="text-sm text-amber-600">
                        ⚠️ {seriesConflictCount} Termin(e) bereits belegt (werden übersprungen)
                      </div>
                    ) : (
                      <div className="text-sm text-green-600">✓ Alle Termine verfügbar</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {!bookingForm.is_series && bookingForm.start_time && bookingForm.end_time && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${
              slotAvailable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {slotAvailable ? (
                <><Check className="h-4 w-4" /><span className="text-sm">Zeitraum verfügbar</span></>
              ) : (
                <><X className="h-4 w-4" /><span className="text-sm">Zeitraum bereits belegt</span></>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={onBook} disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Buche...</>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {bookingForm.is_series ? `${seriesDateCount} Termine buchen` : 'Raum buchen'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
